import { useCallback, useState } from 'react';
import { Button, Stack } from '@mui/material';

import ConnectStatements, {
    type ConnectStatementsLayout,
    type ConnectStatementsRef,
} from 'modules/Onboarding/AccountingSetup/ConnectStatements';
import type { StepHelpers } from 'modules/common/components/StepWizard/StepWizard2';

/**
 * The StepWizard2 wiring for the Auto Statements step — content, footer and the empty left spacer.
 *
 * Lives here rather than in either wizard because the step is owned by a different flow on each side
 * of the V1 redesign gate: the accounting wizard today, business accounts setup once the gate is on
 * (AP-2907). Two copies would be two places for the Skip/Continue behaviour to drift.
 */

function ConnectStatementsContent({
    helpers,
    connectStatementsRef,
    onCanSkipChange,
    layout,
}: Readonly<{
    helpers: StepHelpers;
    connectStatementsRef: React.RefObject<ConnectStatementsRef>;
    onCanSkipChange: ((canSkip: boolean) => void) | undefined;
    layout: ConnectStatementsLayout;
}>) {
    return <ConnectStatements ref={connectStatementsRef} helpers={{ ...helpers, onCanSkipChange }} layout={layout} />;
}

interface ConnectStatementsContentOptions {
    onCanSkipChange?: (canSkip: boolean) => void;
    layout?: ConnectStatementsLayout;
}

export function createConnectStatementsContent(
    connectStatementsRef: React.RefObject<ConnectStatementsRef>,
    { onCanSkipChange, layout = 'cards' }: ConnectStatementsContentOptions = {}
) {
    return function ConnectStatementsContentWrapper(helpers: StepHelpers) {
        return (
            <ConnectStatementsContent
                helpers={helpers}
                connectStatementsRef={connectStatementsRef}
                onCanSkipChange={onCanSkipChange}
                layout={layout}
            />
        );
    };
}

// Empty left spacer so the shared footer omits its default Back button — keeps Skip + Continue grouped
// right (Figma). Module-level for a stable component reference (avoids react/no-unstable-nested-components).
export function ConnectStatementsFooterSpacer() {
    return <span />;
}

function ConnectStatementsFooterButtons({
    canProceed,
    canSkip,
    connectStatementsRef,
    nextLabel,
}: Readonly<{
    canProceed: boolean;
    canSkip: boolean;
    connectStatementsRef: React.RefObject<ConnectStatementsRef>;
    nextLabel: string;
}>) {
    // Local submit state — reading ref.current.isCompleting is non-reactive and would allow double-clicks.
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleContinueClick = useCallback(async () => {
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        try {
            await connectStatementsRef.current?.handleContinue();
        } catch {
            // The mutation hook surfaces the error (snackbar + Datadog); stay on the step to retry.
        } finally {
            setIsSubmitting(false);
        }
    }, [isSubmitting, connectStatementsRef]);

    return (
        <Stack direction="row" alignItems="center" spacing={2}>
            {canSkip && (
                <Button
                    variant="text"
                    color="secondary"
                    disabled={isSubmitting}
                    onClick={() => connectStatementsRef.current?.handleSkip()}>
                    I prefer to upload monthly
                </Button>
            )}
            <Button variant="contained" disabled={!canProceed || isSubmitting} onClick={handleContinueClick}>
                {isSubmitting ? 'Saving...' : nextLabel}
            </Button>
        </Stack>
    );
}

export function createConnectStatementsFooter(
    connectStatementsRef: React.RefObject<ConnectStatementsRef>,
    canSkip: boolean,
    nextLabel = 'Continue'
) {
    return function ConnectStatementsFooter({ canProceed }: { canProceed: boolean }) {
        return (
            <ConnectStatementsFooterButtons
                canProceed={canProceed}
                canSkip={canSkip}
                connectStatementsRef={connectStatementsRef}
                nextLabel={nextLabel}
            />
        );
    };
}
